/**
 * 後台 — 人工建單
 *
 * 場景:客戶 LINE 私訊購買 → 轉帳到位 → admin 代客建單。
 * 流程:
 *   1. 選會員(搜尋 name/phone/email)
 *   2. 選商品 + 數量(只列 active SKU)
 *   3. 填地址(預設帶 member.address,可改)+ 備註 + 初始狀態
 *   4. 送出 → status=paid 觸發 wallet credit hook(若 SKU 有 bottle_count)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminCreateOrder,
  adminListProducts,
  listMembers,
  type AdminProduct,
} from "../../api/admin";
import type { Member } from "../../api/members";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";
import { todayTpe } from "../../utils/date";


export default function OrderCreate() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  // Step 1: 選會員
  const [member, setMember] = useState<Member | null>(null);
  const [memberQuery, setMemberQuery] = useState("");
  const [memberCandidates, setMemberCandidates] = useState<Member[]>([]);
  const [searching, setSearching] = useState(false);

  // Step 2: 選商品
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [cart, setCart] = useState<Record<string, number>>({});

  // Step 3: 地址 + 備註 + 狀態
  const [addressOverride, setAddressOverride] = useState(false);
  const [addressCity, setAddressCity] = useState("");
  const [addressDistrict, setAddressDistrict] = useState("");
  const [addressDetail, setAddressDetail] = useState("");
  const [note, setNote] = useState("");
  // 折讓 / 折扣 — 兩種輸入模式:「折讓金額」直接填折多少;「直接改實收」填實收額,前端換算回折讓
  const [discountMode, setDiscountMode] = useState<"amount" | "final">("amount");
  const [discountInput, setDiscountInput] = useState("");
  const [discountNote, setDiscountNote] = useState("");
  const [initialStatus, setInitialStatus] = useState<"paid" | "shipped" | "pending_payment_mock">("paid");
  const [shipDate, setShipDate] = useState(() => todayTpe());
  const [submitting, setSubmitting] = useState(false);
  // 冪等 key — 存「當前這筆建單意圖」。失敗自動重試沿用同 key(後端冪等,不會重複建單/入帳);
  // 成功才清空,下一筆 = 全新 key。重試仍失敗則保留,使用者稍後再按仍用同 key。
  const idempotencyKey = useRef<string | null>(null);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    // 拉商品清單 — 預先載入,選會員後立刻可挑商品
    (async () => {
      try {
        const all = await adminListProducts();
        setProducts(all.filter((p) => p.active).sort((a, b) =>
          a.category === b.category
            ? a.sort_order - b.sort_order
            : a.category.localeCompare(b.category),
        ));
      } catch {
        showToast("商品載入失敗", "error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // 會員搜尋 — debounce 400ms
  useEffect(() => {
    if (member) return;
    const q = memberQuery.trim();
    if (!q) {
      setMemberCandidates([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await listMembers({ search: q, limit: 10 });
        setMemberCandidates(r.members);
      } catch {
        // ignore
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [memberQuery, member]);

  function pickMember(m: Member) {
    setMember(m);
    setMemberCandidates([]);
    setMemberQuery("");
  }

  function changeQty(productId: string, delta: number) {
    setCart((prev) => {
      const cur = prev[productId] || 0;
      const next = Math.max(0, Math.min(99, cur + delta));
      const out = { ...prev };
      if (next === 0) delete out[productId];
      else out[productId] = next;
      return out;
    });
  }

  const total = useMemo(() => {
    return Object.entries(cart).reduce((sum, [pid, qty]) => {
      const p = products.find((x) => x.id === pid);
      return sum + (p ? p.price * qty : 0);
    }, 0);
  }, [cart, products]);

  const totalBottles = useMemo(() => {
    return Object.entries(cart).reduce((sum, [pid, qty]) => {
      const p = products.find((x) => x.id === pid);
      return sum + (p ? (p.bottle_count || 0) * qty : 0);
    }, 0);
  }, [cart, products]);

  // 折讓金額 — 兩模式最後都換算成「折讓額」,夾在 [0, 原價](實收不會變負)。折讓只動錢,不影響入帳瓶數。
  const discountAmount = useMemo(() => {
    const v = Math.round(Number(discountInput) || 0);
    if (v <= 0) return 0;
    if (discountMode === "amount") return Math.min(v, total);        // 直接填折多少
    return Math.max(0, total - Math.min(v, total));                  // 填實收 → 折讓 = 原價 − 實收
  }, [discountInput, discountMode, total]);
  const finalTotal = Math.max(0, total - discountAmount);

  async function submit() {
    if (!member) {
      showToast("請先選擇會員", "error");
      return;
    }
    if (Object.keys(cart).length === 0) {
      showToast("請選擇商品", "error");
      return;
    }
    if (initialStatus === "shipped" && !shipDate) {
      showToast("請填出貨日期", "error");
      return;
    }
    // 產 / 沿用冪等 key —— ref 為空才產新的,重試與下一次重按都沿用同一個
    if (!idempotencyKey.current) {
      idempotencyKey.current = crypto.randomUUID();
    }

    // 送出一次(帶當前 idempotency_key)。回傳代表後端有回應(成功 or 業務狀態);
    // axios throw(逾時 / 5xx / 斷線)會往外拋,由 submit 接住後重試。
    const postOnce = () =>
      adminCreateOrder({
        member_uid: member.line_user_id,
        items: Object.entries(cart).map(([product_id, qty]) => ({ product_id, qty })),
        address: addressOverride
          ? { city: addressCity, district: addressDistrict, detail: addressDetail }
          : undefined,
        note,
        initial_status: initialStatus,
        ship_date: initialStatus === "shipped" ? shipDate : undefined,
        discount_amount: discountAmount,
        discount_note: discountAmount > 0 ? discountNote.trim() : "",
        idempotency_key: idempotencyKey.current!,
      });

    // 處理後端回應(成功 + 各業務狀態)。回 true = 已收斂(成功),呼叫端清空 key。
    const handleResult = (r: { status: string; order_id?: string }): boolean => {
      if (r.status === "ok" && r.order_id) {
        showToast("人工建單成功", "success");
        navigate(`/admin/orders/${r.order_id}`);
        return true;
      } else if (r.status === "invalid_ship_date") {
        // 業務狀態:輸入問題,非網路失敗 —— 維持既有處理,不重試
        showToast("出貨日期格式不正確", "error");
      } else {
        showToast(`建單未成功:${r.status}`, "error");
      }
      return false;
    };

    setSubmitting(true);
    try {
      try {
        const ok = handleResult(await postOnce());
        if (ok) idempotencyKey.current = null;   // 成功 → 下一筆全新 key
      } catch {
        // 第一次 axios throw —— 用同一個 key 自動重試一次。
        // 後端冪等:第一次若其實已成立,重試拿回同一筆;若真沒成立,重試才建立。
        const ok = handleResult(await postOnce());
        if (ok) idempotencyKey.current = null;
      }
    } catch {
      // 重試仍失敗 —— 用對帳語氣,不宣告失敗;不清空 key(稍後再按仍用同 key,不會重複建單)
      showToast("建單可能已送出,請至訂單列表確認,切勿重複建立", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-6 py-5 flex items-center gap-3">
        <button onClick={() => navigate("/admin/orders")} className="text-white text-xl">‹</button>
        <h1 className="text-xl font-bold">人工建單</h1>
      </header>

      <div className="px-6 mt-6 max-w-3xl space-y-4">
        {/* Step 1: 選會員 */}
        <div className="card">
          <div className="text-sm font-medium mb-3">1. 選擇會員</div>
          {member ? (
            <div className="flex items-center justify-between bg-cream rounded-lg p-3">
              <div>
                <div className="font-medium">{member.name || member.display_name || "—"}</div>
                <div className="text-xs text-muted mt-0.5">
                  {member.phone || "—"} · {member.line_user_id.slice(0, 8)}…
                </div>
              </div>
              <button
                onClick={() => setMember(null)}
                className="text-xs text-coral hover:underline"
              >
                重新選擇
              </button>
            </div>
          ) : (
            <>
              <input
                className="input"
                type="search"
                value={memberQuery}
                onChange={(e) => setMemberQuery(e.target.value)}
                placeholder="搜尋姓名 / 手機 / Email"
              />
              {searching && <div className="text-xs text-muted mt-2">搜尋中…</div>}
              {memberCandidates.length > 0 && (
                <div className="mt-2 max-h-60 overflow-y-auto border border-line rounded-lg">
                  {memberCandidates.map((m) => (
                    <button
                      key={m.line_user_id}
                      onClick={() => pickMember(m)}
                      className="w-full text-left px-3 py-2 border-b border-line/50 last:border-0 hover:bg-cream/50"
                    >
                      <div className="text-sm">{m.name || m.display_name || "—"}</div>
                      <div className="text-xs text-muted">{m.phone || "—"} · {m.email || "—"}</div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Step 2: 選商品(會員選好才出現) */}
        {member && (
          <div className="card">
            <div className="text-sm font-medium mb-3">2. 選擇商品</div>
            {products.length === 0 ? (
              <div className="text-sm text-muted py-4">商品載入中或無上架商品…</div>
            ) : (
              <div className="space-y-2">
                {products.map((p) => {
                  const qty = cart[p.id] || 0;
                  return (
                    <div
                      key={p.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        qty > 0 ? "border-primary bg-primary/5" : "border-line"
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{p.name}</div>
                        <div className="text-xs text-muted">
                          {p.spec} · NT$ {p.price.toLocaleString()}
                          {p.bottle_count > 0 && <span> · {p.bottle_count} 瓶</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => changeQty(p.id, -1)}
                          disabled={qty === 0}
                          className="w-8 h-8 rounded border border-line disabled:opacity-30"
                        >
                          −
                        </button>
                        <span className="w-8 text-center font-bold">{qty}</span>
                        <button
                          onClick={() => changeQty(p.id, 1)}
                          className="w-8 h-8 rounded border border-line"
                        >
                          ＋
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 3: 結帳資訊 */}
        {member && Object.keys(cart).length > 0 && (
          <>
            <div className="card space-y-3">
              <div className="text-sm font-medium">3. 結帳資訊</div>

              <div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addressOverride}
                    onChange={(e) => setAddressOverride(e.target.checked)}
                  />
                  使用其他寄送地址(預設用會員地址)
                </label>
                {addressOverride && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <input
                      className="input"
                      value={addressCity}
                      onChange={(e) => setAddressCity(e.target.value)}
                      placeholder="縣市"
                    />
                    <input
                      className="input"
                      value={addressDistrict}
                      onChange={(e) => setAddressDistrict(e.target.value)}
                      placeholder="區域"
                    />
                    <input
                      className="input col-span-2"
                      value={addressDetail}
                      onChange={(e) => setAddressDetail(e.target.value)}
                      placeholder="詳細地址"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="label">初始狀態</label>
                <select
                  className="input"
                  value={initialStatus}
                  onChange={(e) => setInitialStatus(e.target.value as any)}
                >
                  <option value="paid">已付款待出貨(自動記入存摺)</option>
                  <option value="shipped">已出貨(線下交付完成)</option>
                  <option value="pending_payment_mock">建單未付(僅紀錄,日後再處理)</option>
                </select>
                {initialStatus === "paid" && totalBottles > 0 && (
                  <div className="text-xs text-primary bg-primary/5 px-2 py-1 rounded mt-1">
                    將自動入帳 {totalBottles} 瓶到「{member.name || "此會員"}」的燕窩存摺
                  </div>
                )}
              </div>

              {initialStatus === "shipped" && (
                <div>
                  <label className="label">出貨日期 *</label>
                  <input
                    className="input"
                    type="date"
                    value={shipDate}
                    onChange={(e) => setShipDate(e.target.value)}
                  />
                  <div className="text-xxs text-muted mt-1">預設今天,補登舊單可改</div>
                </div>
              )}

              <div>
                <label className="label">客服備註(選填)</label>
                <textarea
                  className="input resize-none"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="例如:客戶 LINE 私訊購買 / 轉帳末五碼 12345"
                />
              </div>

              <div>
                <label className="label">折讓 / 折扣(選填)</label>
                <div className="flex gap-2">
                  <select
                    className="input w-32 shrink-0"
                    value={discountMode}
                    onChange={(e) => setDiscountMode(e.target.value as "amount" | "final")}
                  >
                    <option value="amount">折讓金額</option>
                    <option value="final">直接改實收</option>
                  </select>
                  <input
                    className="input flex-1 text-right"
                    type="number"
                    min={0}
                    value={discountInput}
                    onChange={(e) => setDiscountInput(e.target.value)}
                    placeholder={discountMode === "amount" ? "折多少(NT$)" : "實收金額(NT$)"}
                  />
                </div>
                {discountAmount > 0 && (
                  <>
                    <div className="text-xs text-muted mt-1">
                      原價 NT$ {total.toLocaleString()} − 折讓 NT$ {discountAmount.toLocaleString()} = 實收 NT$ {finalTotal.toLocaleString()}
                    </div>
                    <input
                      className="input mt-2"
                      value={discountNote}
                      onChange={(e) => setDiscountNote(e.target.value)}
                      placeholder="折讓原因(選填,例:熟客回饋 / 活動折扣)"
                    />
                  </>
                )}
              </div>
            </div>

            {/* 總額卡 */}
            <div className="card bg-primary/5 border-primary/20">
              {discountAmount > 0 && (
                <>
                  <div className="flex items-baseline justify-between text-sm text-muted">
                    <span>原價</span>
                    <span>NT$ {total.toLocaleString()}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-sm text-coral">
                    <span>折讓</span>
                    <span>− NT$ {discountAmount.toLocaleString()}</span>
                  </div>
                </>
              )}
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted">{discountAmount > 0 ? "實收" : "合計"}</span>
                <span className="text-2xl font-bold text-primary">
                  NT$ {finalTotal.toLocaleString()}
                </span>
              </div>
              {totalBottles > 0 && (
                <div className="text-xs text-muted text-right mt-1">折算 {totalBottles} 瓶</div>
              )}
            </div>

            <button
              onClick={submit}
              disabled={submitting}
              className="btn-primary w-full"
            >
              {submitting ? "建單中…" : "確認建單"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
